import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Pencil, Printer, CheckCircle, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';

// ── QC Inspection Report ─────────────────────────────────────────────────────
// Header fields of the incoming-material inspection report, followed by a
// S.No / Test Parameter / Specification / Observation / Result table. The
// parameter rows are pre-filled from the material's product specification when
// one exists, otherwise from this default checklist.
const DEFAULT_QC_PARAMETERS = [
  'Description',
  'Appearance / Visual',
  'Size / Width (mm)',
  'Thickness (micron)',
  'GSM / Weight',
  'Printing & Artwork',
  'Bond / Seal Strength',
  'Roll / Core Condition',
  'Packing & Labelling',
  'Odour',
];

const blankParameter = () => ({ parameter: '', specification: '', observation: '', result: '' });

// "Material inspection and Analysis request" (Format No. WH003-F01-01) records
// 10 GSM/Width samples and 10 individual-grammage readings.
const SAMPLE_COUNT = 10;
const FORMAT_NO = 'WH003-F01-01';
const blankGsmSamples = () => Array.from({ length: SAMPLE_COUNT }, () => ({ gsm: '', width: '' }));
const blankGrammage = () => Array.from({ length: SAMPLE_COUNT }, () => '');
// Pad/trim a stored list back to the fixed number of sample slots.
const toSlots = (list, blank) => {
  const out = blank();
  (list || []).slice(0, SAMPLE_COUNT).forEach((v, i) => { out[i] = v ?? out[i]; });
  return out;
};

const emptyForm = {
  mrn_id: '', overall_result: 'Pass', remarks: '', items: [],
  // Report header
  supplier_name: '', material_name: '', material_code: '',
  invoice_number: '', invoice_date: '', received_date: '',
  quantity_received: '', batch_lot_no: '', mfg_date: '',
  sample_size: '', aql: '1.5', sampling_plan: 'IS 2500 / ANSI Z1.4 – Level II, Normal',
  parameters: [],
  disposition: 'Accepted', inspected_by_name: '', checked_by_name: '',
  // Material Inspection Check points
  coa_received: '', packing_list_received: '', containers_labelled: '', containers_sealed: '',
  material_inwarded_on: '', material_inwarded_by: '',
  // Cleaning of Material / Sample request to QC
  cleaning_done_by: '', material_receipt_number: '', mr_assigned_by: '',
  submitted_by_wh: '', received_by_qc: '',
  // Tests + page-2 sign-offs
  gsm_width_samples: blankGsmSamples(), individual_grammage: blankGrammage(),
  meets_specification: '', performed_by_qc: '', received_by_wh: '', reviewed_by_qa: '',
};

// ISO (YYYY-MM-DD…) → DD/MM/YYYY for the printed report.
const fmtDate = (val) => {
  if (!val) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(val));
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(val);
  return isNaN(d) ? String(val) : d.toLocaleDateString('en-GB');
};
const toDateInput = (val) => (val ? String(val).slice(0, 10) : '');

const QCInspection = () => {
  const [qcRecords, setQcRecords] = useState([]);
  const [mrns, setMrns] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [qcRes, mrnsRes, specRes] = await Promise.all([
        api.get('/purchase/qc'),
        api.get('/purchase/mrn'),
        api.get('/specs/').catch(() => ({ data: [] }))
      ]);
      setQcRecords(qcRes.data);
      setMrns(mrnsRes.data);
      setSpecs(specRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const openCreate = () => {
    setEditing(null);
    setFormData({ ...emptyForm, parameters: [], gsm_width_samples: blankGsmSamples(), individual_grammage: blankGrammage() });
    setShowModal(true);
  };

  const openEdit = (qc) => {
    setEditing(qc);
    setFormData({
      mrn_id: qc.mrn_id,
      overall_result: qc.overall_result || 'Pass',
      remarks: qc.remarks || '',
      items: (qc.items || []).map(it => ({ ...it })),
      supplier_name: qc.supplier_name || '',
      material_name: qc.material_name || '',
      material_code: qc.material_code || '',
      invoice_number: qc.invoice_number || '',
      invoice_date: toDateInput(qc.invoice_date),
      received_date: toDateInput(qc.received_date),
      quantity_received: qc.quantity_received || '',
      batch_lot_no: qc.batch_lot_no || '',
      mfg_date: toDateInput(qc.mfg_date),
      sample_size: qc.sample_size || '',
      aql: qc.aql || '',
      sampling_plan: qc.sampling_plan || '',
      parameters: (qc.parameters || []).map(p => ({ ...blankParameter(), ...p })),
      disposition: qc.disposition || 'Accepted',
      inspected_by_name: qc.inspected_by_name || '',
      checked_by_name: qc.checked_by_name || '',
      coa_received: qc.coa_received || '',
      packing_list_received: qc.packing_list_received || '',
      containers_labelled: qc.containers_labelled || '',
      containers_sealed: qc.containers_sealed || '',
      material_inwarded_on: toDateInput(qc.material_inwarded_on),
      material_inwarded_by: qc.material_inwarded_by || '',
      cleaning_done_by: qc.cleaning_done_by || '',
      material_receipt_number: qc.material_receipt_number || qc.mr_no || '',
      mr_assigned_by: qc.mr_assigned_by || '',
      submitted_by_wh: qc.submitted_by_wh || '',
      received_by_qc: qc.received_by_qc || '',
      gsm_width_samples: toSlots((qc.gsm_width_samples || []).map(s => ({ gsm: s.gsm || '', width: s.width || '' })), blankGsmSamples),
      individual_grammage: toSlots((qc.individual_grammage || []).map(v => v || ''), blankGrammage),
      meets_specification: qc.meets_specification || '',
      performed_by_qc: qc.performed_by_qc || '',
      received_by_wh: qc.received_by_wh || '',
      reviewed_by_qa: qc.reviewed_by_qa || '',
    });
    setShowModal(true);
  };

  const updateSample = (index, field, value) => setFormData(prev => ({
    ...prev,
    gsm_width_samples: prev.gsm_width_samples.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
  }));
  const updateGrammage = (index, value) => setFormData(prev => ({
    ...prev,
    individual_grammage: prev.individual_grammage.map((v, i) => (i === index ? value : v)),
  }));

  // Build the parameter checklist for a material: its active product spec when
  // one exists, otherwise the default incoming-material checklist.
  const parametersForSku = (sku) => {
    const spec = specs.find(s => s.product_sku === sku);
    if (spec && (spec.parameters || []).length) {
      return spec.parameters.map(p => ({
        parameter: p.name || '',
        specification: (p.specification || '').trim()
          || `${[p.min_value, p.target_value, p.max_value].filter(v => v !== '' && v != null).join(' / ')} ${p.unit || ''}`.trim(),
        observation: '', result: '',
      }));
    }
    return DEFAULT_QC_PARAMETERS.map(name => ({ ...blankParameter(), parameter: name }));
  };

  const selectMRN = async (mrnId) => {
    const mrn = mrns.find(m => m.id === mrnId);
    if (!mrn) { setField('mrn_id', mrnId); return; }
    const items = mrn.items.map(item => ({
      sku: item.sku,
      name: item.name,
      received_quantity: item.received_quantity,
      inspected_quantity: item.received_quantity,
      accepted_quantity: item.accepted_quantity,
      rejected_quantity: item.rejected_quantity,
      rejection_reason: '',
      batch_number: item.batch_number || '',
      test_results: {}
    }));
    const first = items[0] || {};
    setFormData(prev => ({
      ...prev,
      mrn_id: mrnId,
      items,
      supplier_name: mrn.vendor_name || prev.supplier_name,
      material_name: first.name || prev.material_name,
      material_code: first.sku || prev.material_code,
      invoice_number: mrn.invoice_number || prev.invoice_number,
      invoice_date: toDateInput(mrn.invoice_date) || prev.invoice_date,
      received_date: toDateInput(mrn.received_date) || prev.received_date,
      batch_lot_no: first.batch_number || prev.batch_lot_no,
      quantity_received: String(items.reduce((s, i) => s + (i.received_quantity || 0), 0)),
      parameters: prev.parameters.length ? prev.parameters : parametersForSku(first.sku),
      // WH003-F01-01: the Material Receipt Number is the MR No. assigned at goods receipt.
      material_receipt_number: mrn.mr_no || mrn.mrn_number || prev.material_receipt_number,
      material_inwarded_on: toDateInput(mrn.received_date) || prev.material_inwarded_on,
    }));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const updateParameter = (index, field, value) => {
    const parameters = formData.parameters.map((p, i) => (i === index ? { ...p, [field]: value } : p));
    setFormData({ ...formData, parameters });
  };
  const addParameter = () => setFormData(prev => ({ ...prev, parameters: [...prev.parameters, blankParameter()] }));
  const removeParameter = (index) => setFormData(prev => ({ ...prev, parameters: prev.parameters.filter((_, i) => i !== index) }));
  const loadDefaultParameters = () => setFormData(prev => ({
    ...prev,
    parameters: parametersForSku((prev.items[0] || {}).sku),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Drop parameter rows the inspector left completely blank.
    const parameters = formData.parameters.filter(p => (p.parameter || '').trim());
    try {
      if (editing) {
        await api.put(`/purchase/qc/${editing.id}`, { ...formData, parameters });
        toast.success('Inspection updated');
      } else {
        await api.post('/purchase/qc', { ...formData, parameters });
        toast.success('Inspection created');
      }
      setShowModal(false);
      setEditing(null);
      setFormData({ ...emptyForm, parameters: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save QC inspection');
    }
  };

  const approveQC = async (qc) => {
    if (!window.confirm(`Approve inspection ${qc.qc_number}? This records your name and the approval time.`)) return;
    try {
      await api.post(`/purchase/qc/${qc.id}/approve`);
      toast.success('Inspection approved');
      fetchData();
    } catch (err) {
      const msg = err.response?.status === 403 ? 'You are not authorized to approve QC inspections' : (err.response?.data?.detail || 'Failed');
      toast.error(msg);
    }
  };

  // ── Material inspection and Analysis request (Format No. WH003-F01-01) ─────
  // The client's controlled 2-page form. Everything the inspection knows is
  // filled in; anything not captured prints as an empty line to be hand-written.
  const printInspectionRequest = (qc) => {
    const samples = toSlots((qc.gsm_width_samples || []).map(s => ({ gsm: s.gsm || '', width: s.width || '' })), blankGsmSamples);
    const grammage = toSlots((qc.individual_grammage || []).map(v => v || ''), blankGrammage);
    const row = (label, value) => `<tr><td colspan="2"><span class="lbl">${label}</span> ${value || ''}</td></tr>`;
    const row2 = (l1, v1, l2, v2) =>
      `<tr><td><span class="lbl">${l1}</span> ${v1 || ''}</td><td><span class="lbl">${l2}</span> ${v2 || ''}</td></tr>`;
    const sec = (label, note) =>
      `<tr class="sec"><td colspan="2">${label}${note ? `<div class="note">${note}</div>` : ''}</td></tr>`;
    const gsmCells = (from) => samples.slice(from, from + 5).map(s =>
      `<td class="gsm"><div><span class="lbl">GSM.:</span> ${s.gsm || ''}</div><div><span class="lbl">Width:</span> ${s.width || ''}</div></td>`
    ).join('');
    const titleBar = `<table class="titlebar"><tr><td><span class="lbl">Title:</span> Material inspection and Analysis request</td></tr></table>`;
    const foot = (n) => `<div class="foot"><div>Format No.: ${FORMAT_NO}</div><div>Page ${n} of 2</div></div>`;

    const w = window.open('', `qc_request_print_${qc.id}`, 'width=900,height=760');
    w.document.open();
    w.document.write(`<html><head><title>Material inspection and Analysis request - ${qc.qc_number}</title>
      <style>
        @page{size:A4;margin:14mm}
        body{font-family:'Times New Roman',Times,serif;font-size:13px;color:#000;padding:4px}
        table{width:100%;border-collapse:collapse}
        td{border:1px solid #000;padding:7px 9px;font-size:13px;vertical-align:top;height:16px}
        .titlebar td{border:1px solid #000;font-weight:bold}
        .titlebar .lbl{color:#1f3864}
        .sec td{text-align:center;font-weight:bold}
        .lbl{font-weight:bold}
        .note{font-weight:normal;font-size:12px}
        .req td{font-weight:normal}
        td.gsm{width:20%}
        tr.g td:first-child{width:34%;text-align:center}
        tr.g td{height:30px}
        .foot{display:flex;justify-content:space-between;margin-top:26px;font-size:12px}
        .page{page-break-after:always}
        .page:last-of-type{page-break-after:auto}
        @media print{button{display:none !important}}
      </style></head><body>

      <div class="page">
        ${titleBar}
        <table style="margin-top:10px">
          ${sec('Material Inspection Check points')}
          ${row('Date of Receipt:', fmtDate(qc.received_date))}
          ${row('Name of the Material:', qc.material_name)}
          ${row('Name of the vendor:', qc.supplier_name)}
          ${row('Certificate of analysis is received:', qc.coa_received)}
          ${row('Total Quantity received:', qc.quantity_received)}
          ${row('Is packing list received:', qc.packing_list_received)}
          ${row('Are the containers/ Rolls / boxes received are labelled:', qc.containers_labelled)}
          ${row('Are Containers/ Rolls / boxes received are sealed/intact:', qc.containers_sealed)}
          ${row('Checked By:', qc.checked_by_name)}
          ${row2('Material Inwarded on:', fmtDate(qc.material_inwarded_on), 'Material Inwarded by:', qc.material_inwarded_by)}
          ${sec('Cleaning of Material')}
          ${row('Cleaning done by:', qc.cleaning_done_by)}
          ${row2('Material Receipt Number :', qc.material_receipt_number || qc.mr_no, 'Assigned by:', qc.mr_assigned_by)}
          ${sec('Raw Material Sample Request to QC')}
          <tr class="req"><td colspan="2">Please perform the Sampling &amp; testing for above material as per applicable specification.</td></tr>
          ${row2('Submitted by (WH):', qc.submitted_by_wh, 'Received by (QC):', qc.received_by_qc)}
        </table>
        <table style="margin-top:-1px">
          <tr class="sec"><td colspan="5">Test for GSM and Width</td></tr>
          <tr>${gsmCells(0)}</tr>
          <tr>${gsmCells(5)}</tr>
        </table>
        ${foot(1)}
      </div>

      <div class="page">
        ${titleBar}
        <table class="grm" style="margin-top:10px">
          <tr class="sec"><td colspan="2">Test for Individual Grammage</td></tr>
          ${grammage.map((v, i) => `<tr class="g"><td>Individual Grammage<br/>${i === 9 ? 'of ' : ''}Sample-${i + 1}</td><td>${v || ''}</td></tr>`).join('')}
          <tr><td colspan="2"><span class="lbl">Is material meeting the specification limits:</span> ${qc.meets_specification ? qc.meets_specification : 'Yes / No'}</td></tr>
          ${row2('Performed By (QC):', qc.performed_by_qc, 'Received by (WH):', qc.received_by_wh)}
          <tr><td colspan="2"><span class="lbl">Reviewed by (QA):</span> ${qc.reviewed_by_qa || qc.approved_by_name || ''}</td></tr>
        </table>
        ${foot(2)}
      </div>

      <button onclick="window.print()" style="margin-top:18px;padding:8px 24px;cursor:pointer">Print</button>
      </body></html>`);
    w.document.close();
  };

  const printQC = (qc) => {
    const po_number = qc.po_number || mrns.find(m => m.id === qc.mrn_id)?.po_number || '-';
    const paramRows = (qc.parameters || []).map((p, i) => `
      <tr>
        <td style="text-align:center">${i + 1}</td>
        <td>${p.parameter || ''}</td>
        <td style="white-space:pre-line">${p.specification || ''}</td>
        <td style="white-space:pre-line">${p.observation || ''}</td>
        <td style="text-align:center">${p.result || ''}</td>
      </tr>`).join('');
    const rows = (qc.items || []).map(it => `
      <tr>
        <td>${it.sku || ''}</td><td>${it.name || ''}</td><td>${it.batch_number || ''}</td>
        <td style="text-align:right">${it.received_quantity ?? ''}</td>
        <td style="text-align:right">${it.inspected_quantity ?? ''}</td>
        <td style="text-align:right">${it.accepted_quantity ?? ''}</td>
        <td style="text-align:right">${it.rejected_quantity ?? ''}</td>
        <td>${it.rejection_reason || ''}</td>
      </tr>`).join('');
    const cell = (label, value) => `<tr><td class="lbl">${label}</td><td>${value || '-'}</td></tr>`;
    const approvalBlock = qc.approved
      ? `<div class="sign"><div>Inspected By<br/><strong>${qc.inspected_by_name || ''}</strong></div>
           <div>Checked By<br/><strong>${qc.checked_by_name || ''}</strong></div>
           <div>QA Approved By<br/><strong>${qc.approved_by_name || ''}</strong><br/>
             <span style="font-size:10px">${qc.approved_at ? new Date(qc.approved_at).toLocaleString('en-GB') : ''}</span></div></div>`
      : `<div class="sign"><div>Inspected By<br/><strong>${qc.inspected_by_name || ''}</strong></div>
           <div>Checked By<br/><strong>${qc.checked_by_name || ''}</strong></div>
           <div>QA Approved By</div></div>`;
    const w = window.open('', `qc_print_${qc.id}`, 'width=900,height=700');
    w.document.open();
    w.document.write(`<html><head><title>${qc.qc_number}</title>
      <style>
        @page{size:A4;margin:12mm}
        body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:12px}
        h1{font-size:17px;margin:0 0 8px;text-align:center;text-decoration:underline}
        .hdr{display:flex;gap:12px}
        .hdr table{width:50%;border-collapse:collapse}
        .hdr td{border:1px solid #666;padding:4px 6px;font-size:11px}
        td.lbl{background:#f1f1f1;font-weight:600;width:44%}
        table.grid{width:100%;border-collapse:collapse;margin-top:10px}
        table.grid th,table.grid td{border:1px solid #666;padding:5px 6px;text-align:left;font-size:11px}
        table.grid th{background:#1a237e;color:#fff}
        h2{font-size:12px;margin:14px 0 0}
        .sign{display:flex;justify-content:space-between;margin-top:38px}
        .sign div{border-top:1px solid #000;padding-top:4px;width:30%;text-align:center;font-size:11px}
        @media print{button{display:none !important}}
      </style></head><body>
      <h1>QC Inspection Report – Incoming Material</h1>
      <div class="hdr">
        <table>
          ${cell('Report / QC No.', qc.qc_number)}
          ${cell('Date of Inspection', fmtDate(qc.inspected_date))}
          ${cell('Supplier Name', qc.supplier_name)}
          ${cell('PO No.', po_number)}
          ${cell('MRN No.', qc.mrn_number)}
          ${cell('MR No.', qc.mr_no)}
          ${cell('Invoice No.', qc.invoice_number)}
          ${cell('Invoice Date', fmtDate(qc.invoice_date))}
        </table>
        <table>
          ${cell('Material Name', qc.material_name)}
          ${cell('Material Code', qc.material_code)}
          ${cell('Batch / Lot No.', qc.batch_lot_no)}
          ${cell('Mfg. Date', fmtDate(qc.mfg_date))}
          ${cell('Date of Receipt', fmtDate(qc.received_date))}
          ${cell('Quantity Received', qc.quantity_received)}
          ${cell('Sample Size', qc.sample_size)}
          ${cell('AQL / Sampling Plan', [qc.aql, qc.sampling_plan].filter(Boolean).join(' – '))}
        </table>
      </div>
      ${paramRows ? `<h2>Test Parameters</h2>
      <table class="grid"><thead><tr>
        <th style="width:44px">S.No</th><th style="width:22%">Test Parameter</th><th>Specification</th><th>Observation</th><th style="width:70px">Result</th>
      </tr></thead><tbody>${paramRows}</tbody></table>` : ''}
      <h2>Material Details</h2>
      <table class="grid"><thead><tr>
        <th>SKU</th><th>Name</th><th>Batch #</th><th>Received</th><th>Inspected</th><th>Accepted</th><th>Rejected</th><th>Rejection Reason</th>
      </tr></thead><tbody>${rows}</tbody></table>
      <div style="margin-top:10px"><strong>Overall Result:</strong> ${qc.overall_result || '-'} &nbsp;&nbsp;
        <strong>Disposition:</strong> ${qc.disposition || '-'} &nbsp;&nbsp;
        <strong>Status:</strong> ${qc.status || '-'}</div>
      ${qc.remarks ? `<div style="margin-top:6px"><strong>Remarks:</strong> ${qc.remarks}</div>` : ''}
      ${approvalBlock}
      <button onclick="window.print()" style="margin-top:20px;padding:8px 24px;cursor:pointer">Print</button>
      </body></html>`);
    w.document.close();
  };

  const getStatusBadge = (status) => {
    const badges = { pending: 'badge-warning', passed: 'badge-success', failed: 'badge-danger' };
    return `badge ${badges[status] || 'badge-info'}`;
  };

  return (
    <div data-testid="qc-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>QC Inspection</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="create-qc-btn">
          <Plus size={16} />
          Create Inspection
        </button>
      </div>

      <div className="card">
        {loading ? (
          <div data-testid="loading-indicator">Loading QC records...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>QC #</th>
                  <th>MRN #</th>
                  <th>MR No.</th>
                  <th>PO #</th>
                  <th>Supplier</th>
                  <th>Material</th>
                  <th>Items</th>
                  <th>Result</th>
                  <th>Status</th>
                  <th>Approval</th>
                  <th>Inspected Date</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {qcRecords.length === 0 ? (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No inspections found</td></tr>
                ) : qcRecords.map((qc) => (
                  <tr key={qc.id} data-testid={`qc-row-${qc.qc_number}`}>
                    <td className="font-mono">{qc.qc_number}</td>
                    <td className="font-mono">{qc.mrn_number}</td>
                    <td className="font-mono">{qc.mr_no || '-'}</td>
                    <td className="font-mono">{qc.po_number || mrns.find(m => m.id === qc.mrn_id)?.po_number || '-'}</td>
                    <td>{qc.supplier_name || '-'}</td>
                    <td>{qc.material_name || '-'}</td>
                    <td>{qc.items?.length || 0} items</td>
                    <td>{qc.overall_result}</td>
                    <td>
                      <span className={getStatusBadge(qc.status)} data-testid={`status-${qc.qc_number}`}>
                        {qc.status}
                      </span>
                    </td>
                    <td>
                      {qc.approved
                        ? <span className="badge badge-success" title={`By ${qc.approved_by_name || ''} on ${qc.approved_at ? new Date(qc.approved_at).toLocaleString('en-GB') : ''}`}>Approved</span>
                        : <span className="badge badge-warning">Pending</span>}
                    </td>
                    <td className="text-sm">{new Date(qc.inspected_date).toLocaleDateString()}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="flex gap-1" style={{ justifyContent: 'flex-end' }}>
                        {!qc.approved && (
                          <button onClick={() => openEdit(qc)} className="btn btn-ghost btn-sm" title="Edit" data-testid={`edit-${qc.qc_number}`}><Pencil size={14} /></button>
                        )}
                        <button onClick={() => printInspectionRequest(qc)} className="btn btn-ghost btn-sm" title="Print Material Inspection & Analysis Request (WH003-F01-01)" data-testid={`print-${qc.qc_number}`}><Printer size={14} /></button>
                        <button onClick={() => printQC(qc)} className="btn btn-ghost btn-sm" title="Print detailed QC Inspection Report" data-testid={`print-report-${qc.qc_number}`}><FileText size={14} /></button>
                        {!qc.approved && (
                          <button onClick={() => approveQC(qc)} className="btn btn-ghost btn-sm" title="QA Approve" data-testid={`approve-${qc.qc_number}`}><CheckCircle size={14} color="#059669" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="qc-modal">
          <div className="modal-content" style={{ maxWidth: '1050px', maxHeight: '92vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? `Edit ${editing.qc_number}` : 'QC Inspection Report'}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">MRN *</label>
                    <select
                      className="form-select"
                      value={formData.mrn_id}
                      onChange={(e) => selectMRN(e.target.value)}
                      required
                      disabled={!!editing}
                      data-testid="mrn-select"
                    >
                      <option value="">Select MRN...</option>
                      {mrns.map((mrn) => (
                        <option key={mrn.id} value={mrn.id}>
                          {mrn.mrn_number}{mrn.mr_no ? ` / ${mrn.mr_no}` : ''} - {mrn.vendor_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Overall Result *</label>
                    <select
                      className="form-select"
                      value={formData.overall_result}
                      onChange={(e) => setField('overall_result', e.target.value)}
                      required
                      data-testid="result-select"
                    >
                      <option value="Pass">Pass</option>
                      <option value="Partial Pass">Partial Pass</option>
                      <option value="Fail">Fail</option>
                    </select>
                  </div>
                </div>

                {/* ── Report header ── */}
                <div style={{ marginTop: 4, padding: '12px', background: 'var(--bg-surface-alt, #f8fafc)', borderRadius: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Inspection Report Details</div>
                  <div className="grid grid-cols-3" style={{ gap: 10 }}>
                    <div className="form-group"><label className="form-label">Supplier Name</label><input type="text" className="form-input" value={formData.supplier_name} onChange={(e) => setField('supplier_name', e.target.value)} data-testid="qc-supplier" /></div>
                    <div className="form-group"><label className="form-label">Material Name</label><input type="text" className="form-input" value={formData.material_name} onChange={(e) => setField('material_name', e.target.value)} data-testid="qc-material" /></div>
                    <div className="form-group"><label className="form-label">Material Code</label><input type="text" className="form-input" value={formData.material_code} onChange={(e) => setField('material_code', e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Invoice No.</label><input type="text" className="form-input" value={formData.invoice_number} onChange={(e) => setField('invoice_number', e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Invoice Date</label><input type="date" className="form-input" value={formData.invoice_date} onChange={(e) => setField('invoice_date', e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Date of Receipt</label><input type="date" className="form-input" value={formData.received_date} onChange={(e) => setField('received_date', e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Batch / Lot No.</label><input type="text" className="form-input" value={formData.batch_lot_no} onChange={(e) => setField('batch_lot_no', e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Mfg. Date</label><input type="date" className="form-input" value={formData.mfg_date} onChange={(e) => setField('mfg_date', e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Quantity Received</label><input type="text" className="form-input" value={formData.quantity_received} onChange={(e) => setField('quantity_received', e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Sample Size</label><input type="text" className="form-input" value={formData.sample_size} onChange={(e) => setField('sample_size', e.target.value)} data-testid="qc-sample-size" /></div>
                    <div className="form-group"><label className="form-label">AQL</label><input type="text" className="form-input" value={formData.aql} onChange={(e) => setField('aql', e.target.value)} placeholder="e.g. 1.5" /></div>
                    <div className="form-group"><label className="form-label">Sampling Plan</label><input type="text" className="form-input" value={formData.sampling_plan} onChange={(e) => setField('sampling_plan', e.target.value)} /></div>
                  </div>
                </div>

                {/* ── Material Inspection Check points (WH003-F01-01) ── */}
                <div style={{ marginTop: 14, padding: '12px', background: 'var(--bg-surface-alt, #f8fafc)', borderRadius: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Material Inspection Check Points</div>
                  <div className="grid grid-cols-2" style={{ gap: 10 }}>
                    {[
                      ['coa_received', 'Certificate of analysis is received'],
                      ['packing_list_received', 'Is packing list received'],
                      ['containers_labelled', 'Are the containers / rolls / boxes labelled'],
                      ['containers_sealed', 'Are containers / rolls / boxes sealed / intact'],
                    ].map(([name, label]) => (
                      <div className="form-group" key={name} style={{ marginBottom: 0 }}>
                        <label className="form-label">{label}</label>
                        <select className="form-select" value={formData[name]} onChange={(e) => setField(name, e.target.value)} data-testid={`qc-${name}`}>
                          <option value="">-</option><option value="Yes">Yes</option><option value="No">No</option>
                        </select>
                      </div>
                    ))}
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Material Inwarded on</label><input type="date" className="form-input" value={formData.material_inwarded_on} onChange={(e) => setField('material_inwarded_on', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Material Inwarded by</label><input type="text" className="form-input" value={formData.material_inwarded_by} onChange={(e) => setField('material_inwarded_by', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Cleaning done by</label><input type="text" className="form-input" value={formData.cleaning_done_by} onChange={(e) => setField('cleaning_done_by', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Material Receipt Number</label><input type="text" className="form-input" value={formData.material_receipt_number} onChange={(e) => setField('material_receipt_number', e.target.value)} data-testid="qc-mr-number" /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Assigned by</label><input type="text" className="form-input" value={formData.mr_assigned_by} onChange={(e) => setField('mr_assigned_by', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Submitted by (WH)</label><input type="text" className="form-input" value={formData.submitted_by_wh} onChange={(e) => setField('submitted_by_wh', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Received by (QC)</label><input type="text" className="form-input" value={formData.received_by_qc} onChange={(e) => setField('received_by_qc', e.target.value)} /></div>
                  </div>
                </div>

                {/* ── Test for GSM & Width / Individual Grammage (10 samples) ── */}
                <div style={{ marginTop: 14, padding: '12px', background: 'var(--bg-surface-alt, #f8fafc)', borderRadius: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Test for GSM &amp; Width and Individual Grammage</div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '4px', width: 70 }}>Sample</th>
                          <th style={{ padding: '4px' }}>GSM</th>
                          <th style={{ padding: '4px' }}>Width</th>
                          <th style={{ padding: '4px' }}>Individual Grammage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.gsm_width_samples.map((s, i) => (
                          <tr key={i}>
                            <td style={{ padding: '4px' }}>Sample-{i + 1}</td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={s.gsm} onChange={(e) => updateSample(i, 'gsm', e.target.value)} style={{ fontSize: 12, padding: 4, width: '100%' }} data-testid={`qc-gsm-${i}`} /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={s.width} onChange={(e) => updateSample(i, 'width', e.target.value)} style={{ fontSize: 12, padding: 4, width: '100%' }} data-testid={`qc-width-${i}`} /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={formData.individual_grammage[i] || ''} onChange={(e) => updateGrammage(i, e.target.value)} style={{ fontSize: 12, padding: 4, width: '100%' }} data-testid={`qc-grammage-${i}`} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="grid grid-cols-4" style={{ gap: 10, marginTop: 10 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Meets specification limits</label>
                      <select className="form-select" value={formData.meets_specification} onChange={(e) => setField('meets_specification', e.target.value)} data-testid="qc-meets-spec">
                        <option value="">-</option><option value="Yes">Yes</option><option value="No">No</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Performed By (QC)</label><input type="text" className="form-input" value={formData.performed_by_qc} onChange={(e) => setField('performed_by_qc', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Received by (WH)</label><input type="text" className="form-input" value={formData.received_by_wh} onChange={(e) => setField('received_by_wh', e.target.value)} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Reviewed by (QA)</label><input type="text" className="form-input" value={formData.reviewed_by_qa} onChange={(e) => setField('reviewed_by_qa', e.target.value)} /></div>
                  </div>
                </div>

                {/* ── Test parameters ── */}
                <div className="form-group" style={{ marginTop: 14 }}>
                  <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                    <label className="form-label" style={{ margin: 0 }}>Test Parameters</label>
                    <div className="flex gap-2">
                      <button type="button" onClick={loadDefaultParameters} className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} data-testid="load-qc-params">Load from Specification</button>
                      <button type="button" onClick={addParameter} className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} data-testid="add-qc-param"><Plus size={12} /> Add Row</button>
                    </div>
                  </div>
                  {formData.parameters.length === 0 ? (
                    <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: 0 }}>No parameters yet — select an MRN, or use “Load from Specification”.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '4px', width: 44 }}>S.No</th>
                            <th style={{ padding: '4px' }}>Test Parameter</th>
                            <th style={{ padding: '4px' }}>Specification</th>
                            <th style={{ padding: '4px' }}>Observation</th>
                            <th style={{ padding: '4px', width: 90 }}>Result</th>
                            <th style={{ padding: '4px', width: 36 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.parameters.map((p, i) => (
                            <tr key={i}>
                              <td style={{ padding: '4px' }} className="tabular-nums">{i + 1}</td>
                              <td style={{ padding: '4px' }}><input type="text" className="form-input" value={p.parameter || ''} onChange={(e) => updateParameter(i, 'parameter', e.target.value)} style={{ fontSize: 12, padding: 4, width: '100%' }} data-testid={`qc-param-name-${i}`} /></td>
                              <td style={{ padding: '4px' }}><textarea className="form-textarea" value={p.specification || ''} onChange={(e) => updateParameter(i, 'specification', e.target.value)} rows={2} style={{ fontSize: 12, padding: 4, width: '100%' }} /></td>
                              <td style={{ padding: '4px' }}><textarea className="form-textarea" value={p.observation || ''} onChange={(e) => updateParameter(i, 'observation', e.target.value)} rows={2} style={{ fontSize: 12, padding: 4, width: '100%' }} data-testid={`qc-param-obs-${i}`} /></td>
                              <td style={{ padding: '4px' }}>
                                <select className="form-select" value={p.result || ''} onChange={(e) => updateParameter(i, 'result', e.target.value)} style={{ fontSize: 12, padding: 4 }}>
                                  <option value="">-</option>
                                  <option value="Pass">Pass</option>
                                  <option value="Fail">Fail</option>
                                  <option value="NA">NA</option>
                                </select>
                              </td>
                              <td style={{ padding: '4px' }}><button type="button" onClick={() => removeParameter(i)} className="btn btn-ghost btn-sm"><Trash2 size={12} color="#DC2626" /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {formData.items.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Inspection Items</label>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '4px' }}>SKU</th>
                            <th style={{ padding: '4px' }}>Name</th>
                            <th style={{ padding: '4px' }}>Batch #</th>
                            <th style={{ padding: '4px' }}>Received</th>
                            <th style={{ padding: '4px' }}>Inspected</th>
                            <th style={{ padding: '4px' }}>Accepted</th>
                            <th style={{ padding: '4px' }}>Rejected</th>
                            <th style={{ padding: '4px' }}>Rejection Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.items.map((item, index) => (
                            <tr key={index}>
                              <td style={{ padding: '4px' }} className="font-mono">{item.sku}</td>
                              <td style={{ padding: '4px' }}>{item.name}</td>
                              <td style={{ padding: '4px' }}>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={item.batch_number || ''}
                                  onChange={(e) => updateItem(index, 'batch_number', e.target.value)}
                                  placeholder="Batch #"
                                  style={{ width: '90px', fontSize: '12px', padding: '4px' }}
                                />
                              </td>
                              <td style={{ padding: '4px' }} className="tabular-nums">{item.received_quantity}</td>
                              <td style={{ padding: '4px' }}>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={item.inspected_quantity}
                                  onChange={(e) => updateItem(index, 'inspected_quantity', parseInt(e.target.value) || 0)}
                                  required
                                  style={{ width: '70px', fontSize: '12px', padding: '4px' }}
                                />
                              </td>
                              <td style={{ padding: '4px' }}>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={item.accepted_quantity}
                                  onChange={(e) => updateItem(index, 'accepted_quantity', parseInt(e.target.value) || 0)}
                                  required
                                  style={{ width: '70px', fontSize: '12px', padding: '4px' }}
                                />
                              </td>
                              <td style={{ padding: '4px' }}>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={item.rejected_quantity}
                                  onChange={(e) => updateItem(index, 'rejected_quantity', parseInt(e.target.value) || 0)}
                                  style={{ width: '70px', fontSize: '12px', padding: '4px' }}
                                />
                              </td>
                              <td style={{ padding: '4px' }}>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={item.rejection_reason}
                                  onChange={(e) => updateItem(index, 'rejection_reason', e.target.value)}
                                  style={{ width: '150px', fontSize: '12px', padding: '4px' }}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-3" style={{ gap: 10 }}>
                  <div className="form-group">
                    <label className="form-label">Disposition</label>
                    <select className="form-select" value={formData.disposition} onChange={(e) => setField('disposition', e.target.value)} data-testid="qc-disposition">
                      <option value="Accepted">Accepted</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Accepted under deviation">Accepted under deviation</option>
                      <option value="Under Test">Under Test</option>
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Inspected By</label><input type="text" className="form-input" value={formData.inspected_by_name} onChange={(e) => setField('inspected_by_name', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Checked By</label><input type="text" className="form-input" value={formData.checked_by_name} onChange={(e) => setField('checked_by_name', e.target.value)} /></div>
                </div>

                <div className="form-group">
                  <label className="form-label">Remarks</label>
                  <textarea
                    className="form-textarea"
                    value={formData.remarks}
                    onChange={(e) => setField('remarks', e.target.value)}
                    data-testid="remarks-input"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => { setShowModal(false); setEditing(null); }} className="btn btn-secondary" data-testid="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" data-testid="submit-qc-btn">
                  {editing ? 'Update Inspection' : 'Submit Inspection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QCInspection;
